import { ChangeEventHandler, FocusEventHandler, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { isWebEDIFormError, WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { WithRef } from 'support/types/withRef';

import { Input } from '../Input/Input/Input';
import { TimestampDateInput } from '../TimestampDateInput/TimestampDateInput';

type SchemableFormInputProps = Omit<Parameters<typeof Input>[0], 'errors' | 'hasError'> & {
  error: WebEDIAnnotation | undefined;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
};
export const SchemableFormInput = ({
  error,
  onBlur,
  onShowFieldErrorModal,
  description,
  variant,
  valueDescription,
  fieldState,
  ref,
  ...otherProps
}: WithRef<SchemableFormInputProps, HTMLInputElement>) => {
  const { t } = useTranslation();
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext();
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const onInputFocus = useCallback(() => {
    setShowNavigableItemPopover(true);
  }, [setShowNavigableItemPopover]);

  const onInputBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      onBlur?.(e);
      setShowNavigableItemPopover(false);
    },
    [onBlur, setShowNavigableItemPopover],
  );

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      otherProps.onChange?.(event);
    },
    [otherProps],
  );

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, onShowFieldErrorModal, showNavigableItemPopover]);

  const InputComponent = otherProps.type === 'date' ? TimestampDateInput : Input;
  if (!otherProps.name) {
    return null;
  }

  const isLargeVariant = variant === 'large';
  const inlineErrors =
    isLargeVariant && isWebEDIFormError(error) ? { message: error.description, type: error.type } : undefined;

  return (
    <div>
      <InputComponent
        {...otherProps}
        value={otherProps.value ?? ''}
        placeholder={
          !otherProps.value && isWebEDIFormError(error) && error.type === 'required'
            ? t('common:table.errors.requiredField')
            : undefined
        }
        onChange={handleOnChange}
        ref={ref}
        wrapperRef={isLargeVariant ? undefined : refs.setReference}
        description={isLargeVariant ? description : undefined}
        errors={inlineErrors}
        fieldState={fieldState}
        onFocus={isLargeVariant ? undefined : onInputFocus}
        onBlur={isLargeVariant ? onBlur : onInputBlur}
        name={otherProps.name}
        data-field={otherProps.name}
        min={otherProps.type === 'number' ? 0 : undefined}
        data-1p-ignore={true}
        {...(isLargeVariant ? { variant, valueDescription } : { variant: 'default' })}
      />

      {!isLargeVariant && showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          viewMode={false}
          annotation={error}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          fieldDescription={description}
          isPendingValidation={fieldState === 'pending-validation'}
        />
      )}
    </div>
  );
};
